/* eslint-disable indent */
import useCompanies from "@/hooks/useCompanies";
import useUsers from "@/hooks/useUsers";
import { authenticationLocalStorage } from "@/utils/constants/localStorage";
import useToastStore from "@/services/store/useToastStore";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessIcon from "@mui/icons-material/Business";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CreateUserPopup from "./components/CreateUserPopup";
import DeleteUserDialog from "./components/DeleteUserDialog";
import EditUserConfigPopup from "./components/EditUserConfigPopup";

const AdminCompany: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { loggedUser } = authenticationLocalStorage;
  const { getCompanyById } = useCompanies();
  const { createUser, deleteUser, updateUserConfig, updateUserPermissions, changeUserCompany } = useUsers();
  const { setToast } = useToastStore();

  const [company, setCompany] = useState<CompanyWithUsersDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [configTarget, setConfigTarget] = useState<User | null>(null);
  const [companyConfigOpen, setCompanyConfigOpen] = useState(false);
  const isAdmin = authenticationLocalStorage.loggedUser?.role === "ADMIN";

  const fetchCompany = () => {
    if (!id) return;
    setLoading(true);
    getCompanyById(id)
      .then(setCompany)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCompany();
  }, [id]);

  const handleCreate = (label: string, role: UserRole, password: string): Promise<void> => {
    if (!id) return Promise.resolve();
    return createUser(label, id, role, password)
      .then(() => {
        setCreateOpen(false);
        fetchCompany();
      })
      .catch(() => {
        setToast({ isOpen: true, type: "error", message: "Erreur lors de la création de l'utilisateur." });
        throw new Error();
      });
  };

  const handleDelete = (): Promise<void> => {
    if (!deleteTarget) return Promise.resolve();
    return deleteUser(deleteTarget.id)
      .then(() => {
        setDeleteTarget(null);
        fetchCompany();
      })
      .catch(() => {
        setToast({ isOpen: true, type: "error", message: "Erreur lors de la suppression de l'utilisateur." });
      });
  };

  const handleSavePermissions = (username: string, permissions: FeaturePermission[]): Promise<void> => {
    const user = company?.users.find((u) => u.alias === username || u.username === username);
    if ( user?.role === "ADMIN") return Promise.resolve();
    return updateUserPermissions(username, permissions)
      .then((updatedUser) => {
        setCompany((prev) =>
          prev
            ? {
                ...prev,
                users: prev.users.map((u) =>
                  u.alias === username || u.username === username
                    ? { ...u, featurePermissions: updatedUser.featurePermissions }
                    : u
                ),
              }
            : prev
        );
      })
      .catch(() => {
        setToast({ isOpen: true, type: "error", message: "Erreur lors de la mise à jour des permissions." });
        throw new Error();
      });
  };

  const handleSwitchCompany = async () => {
    if (!loggedUser) {
      return;
    }

    if (!id) {
      setToast({
        isOpen: true,
        type: "error",
        message: "Entreprise introuvable.",
      });

      return;
    }

    if (company == null) {
      setToast({
        isOpen: true,
        type: "error",
        message: "Entreprise introuvable.",
      });

      return;
    }

    try {
      await changeUserCompany(loggedUser.username, company.id);

      setToast({
        isOpen: true,
        type: "success",
        message: `Vous avez rejoint ${company?.name ?? "l'entreprise"}`,
      });

      // optionnel plus tard
      window.location.reload();
    } 
    catch(error) {

      //console.error(error);

      setToast({
        isOpen: true,
        type: "error",
        message: "Erreur lors du changement d'entreprise.",
      });
    }
  };

  const handleSaveConfig = (username: string, config: UpdateUserConfigDTO): Promise<void> =>
    updateUserConfig(username, config)
      .then((updatedConfig) => {
        // Updates the user list so the user config is up to date
        setCompany((prev) =>
          prev
            ? {
                ...prev,
                users: prev.users.map((u) =>
                  u.alias === username || u.username === username ? { ...u, userConfig: updatedConfig } : u
                ),
              }
            : prev
        );
        setToast({ isOpen: true, type: "success", message: "Configuration mise à jour." });
      })
      .catch(() => {
        setToast({ isOpen: true, type: "error", message: "Erreur lors de la mise à jour de la configuration." });
        throw new Error();
      });

  const columns: GridColDef<User>[] = [
    { field: "username", headerName: "Nom d'utilisateur", flex: 1, sortable: false },
    { field: "alias", headerName: "Alias", flex: 1, sortable: false },
    { field: "role", headerName: "Rôle", flex: 1, sortable: false },
    {
      field: "actions",
      headerName: "",
      width: 100,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row">
          {(row.role !== "ADMIN" && (row.role !== "SUPERVISOR" || loggedUser?.role == "ADMIN"))?  (
            <IconButton size="small" onClick={() => setConfigTarget(row)}>
              <SettingsIcon fontSize="small" />
            </IconButton>
          ) : (
            <></>
          )}


          {(row.id !== loggedUser?.id && row.role !== "ADMIN" && isAdmin)?  (
            <IconButton color="error" size="small" onClick={() => setDeleteTarget(row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          ) : (
            <></>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ padding: 3, boxSizing: "border-box", height: "100%" }}>
      <Stack spacing={2} height="100%">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            {isAdmin && (
              <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/admin")}>
                Retour
              </Button>
            )}
            <Typography variant="h5">{company?.name ?? "Entreprise"}</Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<BusinessIcon />} onClick={() => setCompanyConfigOpen(true)}>
              Configuration d&apos;entreprise
            </Button>
            {isAdmin && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
                Nouvel utilisateur
              </Button>
            )}
            {isAdmin && (
              <Button variant="contained" startIcon={<BusinessIcon />} onClick={() => handleSwitchCompany()}>
                Rejoindre entreprise
              </Button>
            )}
          </Stack>
        </Stack>

        <DataGrid
          rows={company?.users ?? []}
          columns={columns}
          loading={loading}
          disableColumnFilter
          disableRowSelectionOnClick
          hideFooter
        />
      </Stack>
      <CreateUserPopup open={createOpen} onClose={() => setCreateOpen(false)} onCreate={handleCreate} />
      <DeleteUserDialog target={deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} />
      <EditUserConfigPopup
        open={!!configTarget}
        users={configTarget ? [configTarget] : []}
        disablePermissions={false}
        onClose={() => setConfigTarget(null)}
        onSave={handleSaveConfig}
        onSavePermissions={handleSavePermissions}
      />
      <EditUserConfigPopup
        open={companyConfigOpen}
        users={company?.users ?? []}
        title={`Configuration de ${company?.name ?? "Entreprise"}`}
        onClose={() => setCompanyConfigOpen(false)}
        onSave={handleSaveConfig}
        onSavePermissions={handleSavePermissions}
      />
    </Box>
  );
};

export default AdminCompany;
